import { action, computed, makeAutoObservable, toJS } from "mobx";
import { v4 as uuidv4, v4 } from "uuid";

import type { Message, MessageType, ToolCallUIData } from "../../types";
import { DEFAULT_MESSAGE_TYPE } from "../../types";
import { MESSAGE_PLACEHOLDERS } from "../constants";

import type {
  CacheControl,
  CodeContent,
  CodeExecutionResultContent,
  Content,
  FunctionCall,
  Media,
  MediaContent,
  MediaVariable,
  OutputMediaContent,
  TextContent,
  ThinkingContent,
  ToolCall,
} from "@/types";

export class MessageStore {
  private _id: string;
  private _content: Content[];
  private _role: MessageType;
  private _name: string | undefined;
  private _tool_calls = new Map<string, ToolCallUIData>();
  private _cache_control: CacheControl | undefined;
  private _error: string | undefined;
  private _errorDismissed = false;
  private _is_tool_call_in_progress = false;
  private _tool_call_status = "";
  private _has_received_text = false;

  constructor(defaultMessage: Partial<Message> = {}) {
    const {
      content = [{ type: "text", text: "" }],
      role = DEFAULT_MESSAGE_TYPE,
      id = uuidv4(),
    } = defaultMessage;
    this._id = id;
    this._content =
      typeof content === "string" ? [{ type: "text" as const, text: content }] : content;
    this._role = role;
    if (role === "placeholder" && "name" in defaultMessage) {
      this._content = [
        {
          type: "text",
          text: defaultMessage.name as string,
        },
      ];
    }

    if ("name" in defaultMessage && defaultMessage.name) {
      this._name = defaultMessage.name as string;
    }

    if ("tool_calls" in defaultMessage && defaultMessage.tool_calls) {
      const initialToolCalls = defaultMessage.tool_calls as ToolCall[];
      this._tool_calls = new Map(
        initialToolCalls.map(toolCall => {
          const _id = toolCall._id ?? uuidv4();
          return [_id, this._toToolCallUIData(toolCall, _id)];
        }),
      );
    }

    if ("tool_call_id" in defaultMessage && defaultMessage.tool_call_id) {
      this._name = defaultMessage.tool_call_id as string;
    }

    if ("cache_control" in defaultMessage && defaultMessage.cache_control) {
      this._cache_control = defaultMessage.cache_control as CacheControl;
    }

    if ("function_call" in defaultMessage && defaultMessage.function_call) {
      const initialFunctionCall = defaultMessage.function_call as FunctionCall;
      this._role = "assistant";
      this._name = initialFunctionCall.name;
      this._content = [
        {
          type: "text",
          text: initialFunctionCall.arguments,
        },
      ];
    }

    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isToolCallInProgress() {
    return this._is_tool_call_in_progress;
  }

  get hasReceivedText() {
    return this._has_received_text;
  }

  @action setIsToolCallInProgress(isToolCallInProgress: boolean) {
    this._is_tool_call_in_progress = isToolCallInProgress;
  }

  get toolCallStatus() {
    return this._tool_call_status;
  }

  @action setToolCallStatus(toolCallStatus: string) {
    this._tool_call_status = toolCallStatus;
  }

  get id(): string {
    return this._id;
  }

  get tool_calls(): ToolCall[] {
    return Array.from(this._tool_calls.values()).map(toolCallUIData => ({
      tool_id: toolCallUIData?.tool_id,
      id: toolCallUIData.id,
      _id: toolCallUIData._id,
      type: "function" as const,
      function: {
        name: toolCallUIData.name,
        arguments: toolCallUIData.arguments,
      },
      cache_control: toolCallUIData.cache_control,
      ...(toolCallUIData.provider_metadata
        ? { provider_metadata: toolCallUIData.provider_metadata }
        : {}),
    }));
  }

  @action addToolCall(toolCall: ToolCallUIData) {
    const id = uuidv4();
    toolCall._id = id;
    this._tool_calls.set(id, toolCall);
  }

  @action updateToolCall(toolCall: ToolCall) {
    if (toolCall._id) {
      const existing = this._tool_calls.get(toolCall._id);
      this._tool_calls.set(
        toolCall._id,
        this._toToolCallUIData(toolCall, toolCall._id, existing?.provider_metadata),
      );
    }
  }

  @action deleteToolCall(_id: string) {
    this._tool_calls.delete(_id);
  }

  private _toToolCallUIData(
    toolCall: ToolCall,
    _id: string,
    fallbackProviderMetadata?: Record<string, unknown> | null,
  ): ToolCallUIData {
    return {
      tool_id: toolCall.tool_id,
      id: toolCall.id,
      _id,
      name: toolCall.function.name,
      arguments: toolCall.function.arguments,
      cache_control: toolCall.cache_control,
      provider_metadata: toolCall.provider_metadata ?? fallbackProviderMetadata,
    };
  }

  @action setToolCallCacheControl(_id: string, cacheControl: CacheControl | undefined): void {
    const existing = this._tool_calls.get(_id);
    if (existing) {
      this._tool_calls.set(_id, { ...existing, cache_control: cacheControl });
    }
  }

  get cache_control(): CacheControl | undefined {
    return this._cache_control;
  }

  @action setCacheControl(cacheControl: CacheControl | undefined): void {
    this._cache_control = cacheControl;
  }

  @action setName(name: string) {
    this._name = name;
  }

  get name() {
    if (this.role === "placeholder") {
      return this.content;
    }

    return this._name;
  }

  get hasToolCalls(): boolean {
    return this.canAddToolCalls && this._tool_calls.size > 0;
  }

  get canAddToolCalls(): boolean {
    return this._role === "assistant";
  }

  get hasName(): boolean {
    return this._role === "assistant" || this._role === "tool" || this._role === "function";
  }

  get hasMedia(): boolean {
    return this._role === "user" || this._role === "assistant";
  }

  get content(): string {
    const textContent = Array.isArray(this._content)
      ? this._content?.find(content => content.type === "text")?.text || ""
      : "";

    return textContent;
  }

  /**
   * Returns all text content blocks combined into a single string.
   * Use this for copying the full message content.
   */
  get fullTextContent(): string {
    if (!Array.isArray(this._content)) {
      return "";
    }
    return this._content
      .filter((c): c is TextContent => c.type === "text")
      .map(c => c.text)
      .join("\n\n");
  }

  get contentBlocks(): { type: string; content: Content; index: number }[] {
    if (!Array.isArray(this._content)) {
      return [];
    }
    return this._content
      .map((content, index) => ({ type: content.type, content, index }))
      .filter(
        item =>
          item.type === "text" ||
          item.type === "media" ||
          item.type === "media_variable" ||
          item.type === "output_media" ||
          item.type === "code" ||
          (item.type === "thinking" &&
            "thinking" in item.content &&
            Boolean((item.content as { thinking: string }).thinking)) ||
          item.type === "code_execution_result" ||
          item.type === "mcp_list_tools" ||
          item.type === "mcp_call" ||
          item.type === "mcp_approval_request" ||
          item.type === "mcp_approval_response" ||
          item.type === "web_search_tool_result" ||
          item.type === "shell_call" ||
          item.type === "shell_call_output" ||
          item.type === "apply_patch_call" ||
          item.type === "apply_patch_call_output" ||
          item.type === "server_tool_use" ||
          item.type === "bash_code_execution_tool_result" ||
          item.type === "text_editor_code_execution_tool_result",
      );
  }

  @computed
  get all_media_variables(): string[] {
    if (!Array.isArray(this._content)) {
      return [];
    }
    return this._content
      .filter((content): content is MediaVariable => content.type === "media_variable")
      .map(mv => mv.name)
      .filter(name => name && name.length > 0);
  }

  @action
  setTextContentBlockAt(index: number, text: string): void {
    if (!Array.isArray(this._content) || index < 0 || index >= this._content.length) {
      return;
    }
    const content = this._content[index];
    if (content.type === "text") {
      content.text = text;
    }
  }

  @action
  setCacheControlAt(index: number, cacheControl: TextContent["cache_control"] | undefined): void {
    if (!Array.isArray(this._content) || index < 0 || index >= this._content.length) {
      return;
    }
    const content = this._content[index];
    if (content.type === "text") {
      this._content[index] = { ...content, cache_control: cacheControl };
    }
  }

  @action
  deleteContentBlockAt(index: number): void {
    if (!Array.isArray(this._content) || index < 0 || index >= this._content.length) {
      return;
    }
    this._content.splice(index, 1);
  }

  @action
  addTextContentBlock(): void {
    if (!Array.isArray(this._content)) {
      this._content = [];
    }
    this._content.push({ type: "text", text: "" });
  }

  @action
  setApprovalResponseContent(approvalRequestId: string, approve: boolean): void {
    this._content = [
      {
        type: "mcp_approval_response",
        approval_request_id: approvalRequestId,
        approve,
      } as Content,
    ];
    this._role = "user";
  }

  @action
  addFileBlock(): void {
    if (!Array.isArray(this._content)) {
      this._content = [];
    }
    this._content.push({ type: "media", media: { type: "image", url: "", title: "" } });
  }

  @action
  addFileBlockWithMedia(media: Media): void {
    if (!Array.isArray(this._content)) {
      this._content = [];
    }
    this._content.push({ type: "media", media });
  }

  @action
  addMediaVariableBlock(): void {
    if (!Array.isArray(this._content)) {
      this._content = [];
    }
    this._content.push({ type: "media_variable", name: "" });
  }

  @action
  updateMediaBlockAt(index: number, media: Media): void {
    if (!Array.isArray(this._content) || index < 0 || index >= this._content.length) {
      return;
    }
    const content = this._content[index];
    if (content.type === "media") {
      this._content[index] = { type: "media", media };
    }
  }

  @action
  updateMediaVariableBlockAt(index: number, name: string): void {
    if (!Array.isArray(this._content) || index < 0 || index >= this._content.length) {
      return;
    }
    const content = this._content[index];
    if (content.type === "media_variable") {
      this._content[index] = { type: "media_variable", name };
    }
  }

  get mediaContent() {
    return Array.isArray(this._content)
      ? this._content.filter(content => content.type === "media")
      : [];
  }

  get thinkingContent(): string {
    const thinkingContent = Array.isArray(this._content)
      ? this._content?.find(content => content.type === "thinking")?.thinking || ""
      : "";

    return thinkingContent;
  }

  get codeContent(): CodeContent | undefined {
    return Array.isArray(this._content)
      ? this._content?.find((content): content is CodeContent => content.type === "code")
      : undefined;
  }

  get codeExecutionResultContent(): CodeExecutionResultContent | undefined {
    return Array.isArray(this._content)
      ? this._content?.find(
          (content): content is CodeExecutionResultContent =>
            content.type === "code_execution_result",
        )
      : undefined;
  }

  get outputMediaContent(): OutputMediaContent[] {
    return Array.isArray(this._content)
      ? this._content.filter(
          (c): c is OutputMediaContent => c.type === "output_media" && "url" in c,
        )
      : [];
  }

  get function_call(): FunctionCall | undefined {
    if (this._role !== "assistant" || !this.name) {
      return undefined;
    }
    return {
      arguments: this.content,
      name: this.name!,
    };
  }

  setMediaVariable(name: string) {
    const existingMediaVariable = Array.isArray(this._content)
      ? this._content.find((content): content is MediaVariable => content.type === "media_variable")
      : undefined;
    if (existingMediaVariable) {
      existingMediaVariable.name = name;
    } else {
      this._content.push({
        type: "media_variable",
        name,
      });
    }
  }

  deleteMediaAtIndex(index: number) {
    const mediaOffset = Array.isArray(this._content)
      ? this._content.findIndex(content => content.type === "media")
      : -1;

    if (mediaOffset === -1) {
      return;
    }

    this._content.splice((mediaOffset !== -1 ? mediaOffset : 0) + index, 1);
  }

  setSelectedMedia(media?: Media) {
    if (media) {
      this._content.push({
        type: "media",
        media,
      });
    }
  }

  get selected_media(): Media[] | undefined {
    if (!Array.isArray(this._content)) {
      return undefined;
    }

    const existingMedia = this._content
      .filter((content): content is MediaContent => content.type === "media")
      .map(media => media?.media);
    return existingMedia;
  }

  get media_variable(): string | undefined {
    const existingMediaVariable = Array.isArray(this._content)
      ? this._content.find((content): content is MediaVariable => content.type === "media_variable")
      : undefined;

    return existingMediaVariable?.name;
  }

  @computed
  get rawContent(): Content[] {
    // When we have output_media (e.g. image generation), always return full content
    const hasOutputMedia =
      Array.isArray(this._content) &&
      this._content.some((c): c is OutputMediaContent => c.type === "output_media" && "url" in c);
    if (this.role === "assistant" && this.function_call && !hasOutputMedia) {
      return [
        {
          type: "text",
          text: "",
        },
      ];
    } else if (this.role === "placeholder") {
      // Placeholder content is stored in input variables, not in message content
      return [];
    }

    // Handle null/undefined content (e.g., assistant messages with only tool_calls)
    if (!this._content) {
      return [];
    }

    return this._content;
  }

  get role(): MessageType {
    return this._role;
  }

  get usesMonospaceFont(): boolean {
    return !!(
      ((this._role === "assistant" || this._role === "tool" || this._role === "placeholder") &&
        this._name) ||
      this._role === "function"
    );
  }

  get placeholder() {
    const defaultPlaceholder = MESSAGE_PLACEHOLDERS[this._role];

    if (this._name === "function") {
      return defaultPlaceholder;
    }
    if (this.usesMonospaceFont && this._role !== "placeholder") {
      return MESSAGE_PLACEHOLDERS.function;
    }
    return defaultPlaceholder;
  }

  setRole(role: MessageType): void {
    this._role = role;
  }

  @action
  setMessage(message: string): void {
    if (!Array.isArray(this._content)) {
      return;
    }

    const existingTextContent = this._content.find((c): c is TextContent => c.type === "text");

    if (existingTextContent) {
      existingTextContent.text = message;
    } else {
      this._content.push({ type: "text", text: message });
    }
  }

  @action
  setThinkingMessage(message: string): void {
    if (!Array.isArray(this._content)) {
      return;
    }

    const existingThinkingContent = this._content.find(
      c => c.type === "thinking",
    ) as ThinkingContent;

    if (existingThinkingContent) {
      existingThinkingContent.thinking = message;
    } else {
      this._content.push({ type: "thinking", thinking: message });
    }
  }

  @action
  setThinkingContentBlockAt(index: number, thinking: string): void {
    if (!Array.isArray(this._content) || index < 0 || index >= this._content.length) {
      return;
    }
    const content = this._content[index];
    if (content.type === "thinking") {
      (content as ThinkingContent).thinking = thinking;
    }
  }

  replace(
    message: Message & {
      tool_calls?: ToolCall[];
      function_call?: FunctionCall;
    },
  ) {
    if (!Array.isArray(this._content)) {
      return;
    }

    const existingThinkingContent = this._content?.find(content => content.type === "thinking");
    this._content = Array.isArray(message.content) ? message.content : [];
    if (
      !this._has_received_text &&
      Array.isArray(message.content) &&
      message.content.some(
        c => c.type === "text" && "text" in c && typeof c.text === "string" && c.text.length > 0,
      )
    ) {
      this._has_received_text = true;
    }
    if (message.role) {
      this._role = message.role;
    }
    const messageFunctionCall = message.function_call as FunctionCall;

    if (messageFunctionCall) {
      this._role = "assistant";
      this._content = [
        {
          type: "text",
          text: messageFunctionCall.arguments,
        },
      ];

      if (existingThinkingContent && !this._content.some(c => c.type === "thinking")) {
        this._content.push(existingThinkingContent);
      }
      this._name = messageFunctionCall.name;
      return;
    }

    if (existingThinkingContent && !this._content?.some(c => c.type === "thinking")) {
      this._content.push(existingThinkingContent);
    }

    const initialToolCalls = message.tool_calls as ToolCall[];
    if (initialToolCalls) {
      this._tool_calls = new Map(
        initialToolCalls.map(toolCall => {
          const _id = toolCall._id ?? uuidv4();
          return [_id, this._toToolCallUIData(toolCall, _id)];
        }),
      );
    }

    if (message.tool_call_id) {
      this._name = message.tool_call_id;
    }

    if ("cache_control" in message && message.cache_control) {
      this._cache_control = message.cache_control as CacheControl;
    }
  }

  @action
  clone(): MessageStore {
    const rawMessage = this.toMessage();
    // Use _content directly to avoid any serialization issues with MobX observables
    let content: Content[] = [];
    if (Array.isArray(this._content)) {
      content = toJS(this._content);
    } else if (Array.isArray(rawMessage.content)) {
      content = toJS(rawMessage.content);
    }
    const normalizedContent = Array.isArray(content) ? content : [];
    const hasOutputMedia =
      normalizedContent.length > 0 &&
      normalizedContent.some(
        (c): c is OutputMediaContent => c.type === "output_media" && "url" in c,
      );
    // For Assistant messages with output_media (e.g. image generation), pass only content/role.
    // This ensures the popped-in message displays the same as the response (no Function Name/Tool Call UI).
    if (hasOutputMedia) {
      return new MessageStore({
        content: normalizedContent,
        role: rawMessage.role,
        id: v4(),
      });
    }
    return new MessageStore({
      ...rawMessage,
      content: normalizedContent,
      tool_calls: rawMessage.tool_calls ? toJS(rawMessage.tool_calls) : undefined,
      id: v4(),
    });
  }

  toMessage(): Message {
    const message: Message = {
      content: this._content,
      role: this._role,
      id: this._id,
      tool_calls: this.tool_calls,
      function_call: this.function_call,
    };
    if (this.role === "tool" && this.name) {
      message.tool_call_id = this.name;
    }
    if (this.role === "tool" && this._cache_control) {
      message.cache_control = this._cache_control;
    }
    return message;
  }

  get schema() {
    const identifier = this.role === "tool" ? { tool_call_id: this.name } : { name: this.name };
    return {
      role: this.role as MessageType,
      content: this.rawContent,
      ...identifier,
      tool_calls: this.tool_calls || [],
      function_call: this.function_call,
      ...(this.role === "tool" && this._cache_control
        ? { cache_control: this._cache_control }
        : {}),
    };
  }

  @action setParsingError(error: string) {
    if (this._error !== error) {
      this._error = error;
      this._errorDismissed = false;
    }
  }

  @action dismissParsingError() {
    this._errorDismissed = true;
  }

  get parsingError() {
    if (this._errorDismissed) {
      return undefined;
    }
    return this._error;
  }

  get hasApprovalResponseContent(): boolean {
    return (
      Array.isArray(this._content) && this._content.some(c => c.type === "mcp_approval_response")
    );
  }

  get hasMcpContent(): boolean {
    return (
      Array.isArray(this._content) &&
      this._content.some(
        c =>
          c.type === "mcp_list_tools" || c.type === "mcp_call" || c.type === "mcp_approval_request",
      )
    );
  }

  get hasShellContent(): boolean {
    return (
      Array.isArray(this._content) &&
      this._content.some(c => c.type === "shell_call" || c.type === "shell_call_output")
    );
  }

  get hasApplyPatchContent(): boolean {
    return (
      Array.isArray(this._content) &&
      this._content.some(c => c.type === "apply_patch_call" || c.type === "apply_patch_call_output")
    );
  }

  get isEmptyMessage() {
    return (
      this.content === "" &&
      !this.codeContent &&
      this.role === "assistant" &&
      !this.hasToolCalls &&
      !this.hasMcpContent &&
      !this.hasShellContent &&
      !this.hasApplyPatchContent &&
      this.mediaContent.length === 0 &&
      this.outputMediaContent.length === 0
    );
  }
}

export type IMessageStore = InstanceType<typeof MessageStore>;
